<template>
    <div class="d-flex align-items-center">
        <div class="mb-0 w-100">
            <b-media v-show="showObjectiveDetails" @click="showKr = !showKr" no-body class="border-start-objective">
                <b-media-aside vertical-align="center" class="user-picture mr-0">
                    <UserPicture :user="objective.user" />
                </b-media-aside>
                <b-container
                    fluid
                    tag="b-media-body"
                    align-v="center"
                    class="p-0"
                    id="btn-view-krs"
                    :disabled="!isThereKeyResults"
                >
                    <b-row no-gutters align-v="center">
                        <b-col lg="12" xl="5" class="collapse-toggle">
                            <b-row no-gutters>
                                <b-col class="col-11 d-flex">
                                    <div>
                                        {{ objective.title }}
                                    </div>
                                </b-col>
                            </b-row>
                        </b-col>
                        <b-col lg="12" xl="7" class="mt-2 mt-xl-0">
                            <b-row class="data">
                                <div class="obj group-items w-100 alignment-fix">
                                    <b-link :href="goToObjectivePage(objective.id)" target="_blank">
                                        <img src="@/assets/icons/arrow-up-right.svg" />
                                    </b-link>

                                    <ProgressBar
                                        class="progress"
                                        slot="progress-bar"
                                        :value="objectiveProgress"
                                        height="1rem"
                                        use-default-label
                                    />
                                </div>
                                <div>
                                    <StatusCounter
                                        v-if="isThereKeyResults && hasDaily"
                                        :statusCounter="statusCounter"
                                        :idTextAppend="`objective-${objective.id}`"
                                    />
                                </div>

                                <div class="obj group-items details">
                                    <img v-b-tooltip.hover :title="levelSection" :src="levelIcon" />
                                    <img
                                        v-b-tooltip.hover
                                        :title="objective.period.end_date | formatDate"
                                        src="@/assets/icons/calendar-check.svg"
                                    />
                                    <OKRsChartBtn
                                        v-b-tooltip.hover
                                        :title="$t('tree.name')"
                                        class="okr-chart"
                                        slot="okrs-chart-btn"
                                        id="btn-go-to-okrs-chart"
                                        :objectiveId="objective.id"
                                    />

                                    <Dropdown
                                        :id="`dropdown-actions-of-objective-${objective.id}`"
                                        slot="dropdown"
                                        :on-edit-callback="onEdit"
                                        :on-delete-callback="onDelete"
                                        :on-request-collaboration-callback="onRequestCollaborationCallback"
                                        :on-duplicate-callback="onDuplicate"
                                        :can-duplicate="canDuplicateOKR"
                                        :disabled-tooltip-label="$t('hasNotActionsAuthorization')"
                                        :disabled="notAllowedToPerformActions()"
                                        :has-not-edit-permission="!objective.update_permission"
                                        :has-not-duplicate-permission="!objective.duplicate_permission"
                                        :has-not-request-collaboration-permission="
                                            !objective.request_collaboration_permission
                                        "
                                        :has-not-delete-permission="!objective.delete_permission"
                                        class="dropdown"
                                    />
                                </div>
                            </b-row>
                        </b-col>
                    </b-row>
                </b-container>
            </b-media>
            <b-container fluid v-if="isThereKeyResults" class="p-0">
                <b-row :class="showObjectiveDetails ? 'ml-2' : ''">
                    <b-col>
                        <b-collapse v-model="showKeyResultCollapse" id="collapse" class="p-0">
                            <KeyResultDetails
                                v-for="(keyResult, index) in objective.key_results"
                                :key="index"
                                :key-result="keyResult"
                                :objective="objective"
                                @onDeletedKeyResult="$emit('onDeletedKeyResult', $event)"
                                :okriFilter="okriFilter"
                                :initiativesFilter="initiativesFilter"
                                @onChangeInitiative="$emit('onChangeInitiative')"
                                :periodStatus="objective.period.status"
                                :expandAllToggle="expandAllToggle"
                            />
                        </b-collapse>
                    </b-col>
                </b-row>
            </b-container>
            <b-container fluid v-if="showObjectiveDetails" class="create-kr-btn-wraper">
                <b-collapse v-model="showKr" id="collapse">
                    <div
                        v-show="showKeyResultsDetails"
                        id="btn-all-okrs-new-kr"
                        class="pt-1 border-start-key-result"
                        @click="$emit('onClickNewKeyResult', objective)"
                    >
                        <img src="@/assets/icons/create-initiative.svg" />
                        {{ $t('okr.createKeyResult') }}
                    </div>
                </b-collapse>
            </b-container>
        </div>
    </div>
</template>

<script>
import { KeyResultDetails } from '@/views/okr/daily'
import { UserPicture, Dropdown, OKRsChartBtn } from '@/views/okr/components'
import { deleteObjective as deleteObjectiveFromAPI } from '@/views/okr/objective/api.js'
import commonMessages from '@/views/okr/common-messages'
import { danger, success } from '@/lib/notification'
import { getV1PathWithOrigin } from '@/router/routesToV1'
import { proceedWithConfirmation } from '@/lib/sweet-alert'
import ProgressBar from '@/views/components/progress-bar/ProgressBar'
import { getDateWithTime } from '@/lib/date'
import { userPermissionsMixin } from '@/mixins'
import StatusCounter from '@/views/okr/daily/statusCounter/StatusCounter.vue'
import { includes } from 'lodash'
import { mapState } from 'vuex'

export default {
    name: 'DailyObjectiveDetails',
    mixins: [userPermissionsMixin],
    components: {
        KeyResultDetails,
        ProgressBar,
        Dropdown,
        UserPicture,
        OKRsChartBtn,
        StatusCounter,
    },
    props: {
        objective: {
            type: Object,
            required: true,
        },
        okriFilter: {
            type: Array,
        },
        initiativesFilter: {
            type: Array,
        },
        expandAllToggle: {
            type: Boolean,
        },
    },
    computed: {
        showKeyResultCollapse: {
            get() {
                return this.showObjectiveDetails ? this.showKr : true
            },
            set(val) {
                return val
            },
        },
        objectiveProgress() {
            return this.objective.progress.toFixed(2) * 100
        },
        isThereKeyResults() {
            return this.objective.key_results.length > 0
        },
        ...mapState('profile', ['user']),
        hasDaily() {
            return includes(this.user.has_modules, 'has_daily')
        },
        showObjectiveDetails() {
            return this.okriFilter.includes('objectives')
        },
        showKeyResultsDetails() {
            return this.okriFilter.includes('key-results')
        },
        levelIcon() {
            let icon = ''
            switch (this.objective.level) {
                case 'unit':
                    icon = 'unit'
                    break
                case 'department':
                    icon = 'department'
                    break
                default:
                    icon = 'personal'
                    break
            }
            return require(`@/assets/icons/${icon}.svg`)
        },
        levelSection() {
            let section = ''
            if (this.objective.level === 'personal') section = this.$t('groupLevel.personal')
            else section = this.objective.section.name

            return section
        },
        canDuplicateOKR() {
            const { currentUser, objective } = this
            const availableRoles = ['god', 'administrator', 'local_administrator', 'is_okr_leader']

            return (
                this.userHasPermission(currentUser, availableRoles, []) ||
                this.isOwnerOfObjetive(objective.user.id, currentUser.id) ||
                this.isManagerOfOwner(objective.user.manager_id, currentUser.manager_id)
            )
        },
        currentUser() {
            return this.$store.state.currentUser.user
        },
    },
    data() {
        return {
            showKr: false,
            statusCounter: {},
        }
    },
    filters: {
        formatDate: function (value) {
            return getDateWithTime(value, 'DD/MM/YYYY')
        },
    },
    watch: {
        objective() {
            this.setStatusCounterData()
        },
        expandAllToggle(val) {
            this.showKr = val
        },
    },
    mounted() {
        this.setStatusCounterData()
    },
    methods: {
        setStatusCounterData() {
            if (this.objective.key_results.length) {
                let krs = this.objective.key_results.filter((kr) => {
                    return kr.initiatives.length > 0
                })

                let initiatives = []

                krs.forEach((kr) => {
                    initiatives.push(...kr.initiatives)
                })

                if (initiatives.length) {
                    const toDo = initiatives.filter((initiative) => {
                        return initiative.status === 'todo'
                    }).length

                    const doing = initiatives.filter((initiative) => {
                        return initiative.status === 'doing'
                    }).length

                    const done = initiatives.filter((initiative) => {
                        return initiative.status === 'done'
                    }).length

                    this.statusCounter = {
                        toDo,
                        doing,
                        done,
                    }
                }
            }
        },
        goToObjectivePage(objectiveId) {
            return `${getV1PathWithOrigin('/okr/objectives', { objectiveId })}`
        },
        onEdit() {
            this.$router.push({
                path: `${this.$route.path}/objective/${this.objective.id}/edit`,
            })
        },
        onDelete() {
            proceedWithConfirmation({
                type: 'warning',
                icon: 'warning',
                title: this.$t('okr.modal.objective.deleteConfirmation.title'),
                message: this.$t('okr.modal.objective.deleteConfirmation.message'),
                confirmButtonText: this.$t('okr.modal.objective.deleteConfirmation.confirmButtonText'),
                onConfirm: () => {
                    return deleteObjectiveFromAPI(this.objective.id)
                },
                callback: () => {
                    success(this.$t('okr.modal.objective.deleteSuccess.title'))
                    this.$emit('onDeletedObjective')
                },
                onError: () => {
                    danger(this.$t('okr.modal.objective.deleteError.message'))
                },
            })
        },
        onRequestCollaborationCallback() {
            this.$router.push({
                path: `${this.$route.path}/objective/${this.objective.id}/request`,
            })
        },
        onDuplicate() {
            this.$router.push({
                path: `${this.$route.path}/objective/${this.objective.id}/duplicate`,
            })
        },
        isOwnerOfObjetive(user, currentUser) {
            return user.id === currentUser.id
        },
        isManagerOfOwner(user, currentUser) {
            return user.manager_id === currentUser.manager_id
        },
        notAllowedToPerformActions() {
            return (
                !this.objective.update_permission &&
                !this.objective.duplicate_permission &&
                !this.objective.request_collaboration_permission &&
                !this.objective.delete_permission
            )
        },
    },
    i18n: {
        messages: {
            'en-US': {
                period: '<strong>Period:</strong> {period}',
                emptyKr: 'This objective has no defined key results.',
                hasNotActionsAuthorization: 'You are not authorized to perform actions on this Objective',
            },
            'es-ES': {
                period: '<strong>Período:</strong> {period}',
                emptyKr: 'Este objetivo no tiene resultados clave definidos.',
                hasNotActionsAuthorization: 'No está autorizado para realizar acciones en este Objetivo',
            },
            'pt-BR': {
                period: '<strong>Período:</strong> {period}',
                emptyKr: 'Este objetivo não possui resultados chave definidos.',
                hasNotActionsAuthorization: 'Você não tem autorização para realizar ações neste Objetivo',
            },
        },
        sharedMessages: commonMessages,
    },
}
</script>
<style scoped>
::v-deep .border-start-objective {
    border-left: 4px solid #022b4f;
    padding-left: 0.7rem !important;
    padding: 0.3rem 0 0.3rem 0;
    align-items: center !important;
}

::v-deep .border-start-objective:hover {
    background: #ececec;
}

::v-deep .data {
    display: flex;
    width: 100%;
    justify-content: space-between;
    align-items: center;
    box-sizing: border-box;
}

.data .group-items {
    display: flex;
    gap: 1rem;
    align-items: center;
}

@media screen and (max-width: 1750px) {
    .data .group-items {
        display: flex;
        gap: 0.8rem;
        align-items: center;
    }
}

.data .w-100 {
    width: 65% !important;
}

.empty-key-results {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    vertical-align: middle;
    color: #444444;
    padding-left: 0.5rem;
}

.empty-key-results img {
    width: 0.8rem;
}

#btn-new-kr {
    margin-left: 3rem;
}

::v-deep .collapse-toggle {
    cursor: pointer;
}

#btn-all-okrs-new-kr {
    display: flex;
    gap: 0.5rem;
    align-content: center;
    color: #009ada;
    font-family: 'Roboto';
    font-style: normal;
    font-weight: 400;
    font-size: 0.9rem;
    padding-top: 0.6rem !important;
    padding-bottom: 0.6rem;
    cursor: pointer;
}
#btn-daily-initiatives-profile-new-kr:hover {
    background: #ececec;
}

::v-deep .border-start-key-result {
    width: 100%;
    border-left: 4px solid #009ada !important;
    padding-left: 0.7rem !important;
    padding: 0.3rem 0 0.3rem 0;
}

.create-kr-btn-wraper {
    padding-left: 1rem;
    margin-left: 0.45rem;
    margin-top: 0.5rem;
    margin-bottom: 0.8rem;
}

.user-picture:hover {
    cursor: pointer;
}

::v-deep .okr-chart {
    padding: 0.1rem 0.2rem;
}

::v-deep .okr-chart img {
    width: 1rem;
}

.data .alignment-fix {
    padding-left: 0.86rem;
}
</style>
